// © 2026 Holmes Computer Consultants. All rights reserved.
import { LOD, Mesh, type Object3D, type BufferAttribute, type Vector3 } from 'three'

type Geometry = { mesh: Mesh; position: BufferAttribute; normal?: BufferAttribute; rest: Float32Array; normals?: Float32Array; contact: boolean }
type Primitive = { mesh: Mesh; position: Vector3; scale: Vector3; foot: boolean; height: number }
const fits = new WeakMap<Object3D, CabinetFloorFit>()

/** Register only this table's uploaded support geometry. No floor, body or controls move. */
export function registerCabinetFloor(root: Object3D, cabinet: Object3D, bottom: number): void {
  fits.set(root, new CabinetFloorFit(root, cabinet, bottom))
}
export function fitCabinetToFloor(root: Object3D, floor?: number): void { fits.get(root)?.refresh(floor) }

export class CabinetFloorFit {
  readonly parts: Geometry[] = []
  readonly primitives: Primitive[] = []
  valid = true
  uploads = 0
  private height = NaN
  private scale = NaN
  private scaleX = NaN
  private scaleZ = NaN
  private tiltX = NaN
  private tiltZ = NaN
  private floor: number | undefined
  private readonly upper: number
  private readonly foot: number
  private readonly lod: LOD | undefined
  constructor(private readonly root: Object3D, cabinet: Object3D, bottom: number) {
    this.upper = bottom - .032; this.foot = Math.min(.060, this.upper * .4)
    const detail = cabinet.getObjectByName('cabinet.leg.detail')
    this.lod = detail instanceof LOD ? detail : undefined
    cabinet.traverse(object => {
      if (!(object instanceof Mesh)) return
      if (object.userData['floorSupport'] !== undefined) {
        object.geometry.computeBoundingBox()
        const bounds = object.geometry.boundingBox!
        this.primitives.push({ mesh: object, position: object.position.clone(), scale: object.scale.clone(), foot: object.userData['floorSupport'] === 'foot', height: bounds.max.y - bounds.min.y })
      } else if (object.name.startsWith('cabinet.leg.') || object.name === 'contact.floor') {
        const position = object.geometry.getAttribute('position') as BufferAttribute, normal = object.geometry.getAttribute('normal') as BufferAttribute | undefined
        this.parts.push({ mesh: object, position, rest: new Float32Array(position.array), ...(normal ? { normal, normals: new Float32Array(normal.array) } : {}), contact: object.name === 'contact.floor' })
      }
    })
  }
  refresh(floor?: number): void {
    const height = this.root.position.y, scale = this.root.scale.y
    if (this.height === height && this.scale === scale && this.floor === floor && this.scaleX === this.root.scale.x && this.scaleZ === this.root.scale.z && this.tiltX === this.root.rotation.x && this.tiltZ === this.root.rotation.z) return
    const ground = floor === undefined ? 0 : (floor - height) / scale
    const stretch = (this.upper - ground - this.foot) / (this.upper - this.foot)
    this.valid = Number.isFinite(height) && Number.isFinite(scale) && scale > 0
      && this.root.scale.x === scale && this.root.scale.z === scale && (floor === undefined || Number.isFinite(floor))
      && this.upper > this.foot && stretch > 0 && Math.abs(this.root.rotation.x) < .0001 && Math.abs(this.root.rotation.z) < .0001
      && this.primitives.every(p => p.foot || p.height > ground)
    const fit = floor !== undefined && this.valid
    for (const part of this.parts) {
      for (let at = 0; at < part.rest.length; at += 3) {
        const y = part.rest[at + 1]!, nx = part.normals?.[at] ?? 0, ny = part.normals?.[at + 1] ?? 1, nz = part.normals?.[at + 2] ?? 0
        const shaft = !part.contact && y > this.foot && y < this.upper
        part.position.setXYZ(at / 3, part.rest[at]!, !fit ? y : part.contact ? ground + y / scale : y <= this.foot ? y + ground : shaft ? ground + this.foot + (y - this.foot) * stretch : y, part.rest[at + 2]!)
        const yy = fit && shaft ? ny / stretch : ny, length = fit && shaft ? Math.hypot(nx, yy, nz) : 1
        part.normal?.setXYZ(at / 3, nx / length, yy / length, nz / length)
      }
      part.position.needsUpdate = true; if (part.normal) part.normal.needsUpdate = true
      part.mesh.geometry.computeBoundingBox(); part.mesh.geometry.computeBoundingSphere(); this.uploads++
    }
    for (const p of this.primitives) {
      p.mesh.position.copy(p.position); p.mesh.scale.copy(p.scale)
      if (fit) {
        p.mesh.position.y += p.foot ? ground : ground / 2
        if (!p.foot) p.mesh.scale.y *= (p.height - ground) / p.height
      }
    }
    if (this.lod) {
      const near = this.parts.filter(p => p.mesh.name.includes('.near.'))
      if (near.length) {
        const min = Math.min(...near.map(p => p.mesh.geometry.boundingBox!.min.y))
        const max = Math.max(...near.map(p => p.mesh.geometry.boundingBox!.max.y))
        // Move the LOD distance reference and compensate the child transform together.
        // The support vertices stay in cabinet coordinates at every detail level.
        const centre = (min + max) / 2
        this.lod.position.y = centre
        for (const level of this.lod.levels) level.object.position.y = -centre
      }
    }
    this.height = height; this.scale = scale; this.floor = floor
    this.scaleX = this.root.scale.x; this.scaleZ = this.root.scale.z; this.tiltX = this.root.rotation.x; this.tiltZ = this.root.rotation.z
  }
}
